import { Plug, BarChart2, Trophy, type LucideIcon } from "lucide-react";
import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type Variants,
} from "framer-motion";
import useTranslation from "../../client/hooks/useTranslation";

/**
 * HowItWorks — "Comment ça marche" section.
 *
 * A genuine ordered 3-step process (connect → analyze → perform): the giant
 * sequence numbers and the connecting spine carry information, not decoration.
 *
 * The section is intentionally locked to the dark palette (#0F1729) for visual
 * continuity with the rest of the marketing page — colors are hardcoded by
 * design rather than driven by theme tokens. All prose is i18n.
 *
 * Motion is orchestrated, not uniform: the central spine draws as the section
 * scrolls into view, each step reveals from its own side, and every bespoke
 * visual animates the thing it shows (sync pulse, bars filling, payout bloom).
 * A full prefers-reduced-motion path ships the section static and complete.
 */

const ACCENT = "#00C9A7";
const BLUE = "#3D6EFF";
const RED = "#E24B4A";
const AMBER = "#EF9F27";

// Reveal viewport config — fire once, slightly before the block is fully in view.
const VIEWPORT = { once: true, amount: 0.35 } as const;
const EASE = [0.16, 1, 0.3, 1] as const; // ease-out-expo

const fadeInFrom = (dir: "left" | "right", reduce: boolean): Variants => ({
  hidden: reduce ? { opacity: 1 } : { opacity: 0, x: dir === "left" ? -44 : 44 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: EASE },
  },
});

interface StepConfig {
  number: string;
  icon: LucideIcon;
  key: "step1" | "step2" | "step3";
}

const STEPS: StepConfig[] = [
  { number: "01", icon: Plug, key: "step1" },
  { number: "02", icon: BarChart2, key: "step2" },
  { number: "03", icon: Trophy, key: "step3" },
];

export default function HowItWorks() {
  const t = useTranslation();
  const tw = t.landing.howItWorks;
  const reduce = useReducedMotion() ?? false;

  const sectionRef = useRef<HTMLDivElement>(null);
  // Draw the central spine as the steps scroll through the viewport.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 75%", "end 60%"],
  });
  const spineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      className="relative isolate border-t overflow-x-clip mx-[calc(-50vw+50%)] w-screen"
      style={{
        backgroundColor: "#0F1729",
        borderTopColor: "rgba(255,255,255,0.06)",
      }}
    >
      {/* ── Atmosphere ──────────────────────────────────────────────────── */}
      {/* Teal aurora bloom, offset to follow the eye down the timeline */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 18% 28%, rgba(0,201,167,0.10), transparent 70%)," +
            "radial-gradient(55% 45% at 85% 78%, rgba(61,110,255,0.08), transparent 72%)",
        }}
      />
      {/* Faint terminal dot-grid texture */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage:
            "radial-gradient(75% 60% at 50% 40%, black, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(75% 60% at 50% 40%, black, transparent 90%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        {/* Header */}
        <motion.div
          className="mx-auto max-w-2xl text-center"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p
            className="font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              color: ACCENT,
              marginBottom: "16px",
            }}
          >
            {tw.overline}
          </p>
          <h2
            className="font-display font-bold text-white text-[30px] sm:text-[40px]"
            style={{ lineHeight: 1.12, letterSpacing: "-0.02em", textWrap: "balance" }}
          >
            {tw.title}
          </h2>
          <p
            className="mx-auto mt-4"
            style={{
              fontSize: "15px",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.55)",
              maxWidth: "490px",
              textWrap: "pretty",
            }}
          >
            {tw.subtitle}
          </p>
        </motion.div>

        {/* Steps */}
        <div ref={sectionRef} className="relative mt-20 flex flex-col gap-16 lg:mt-28 lg:gap-0">
          {/* Central spine — desktop only. A static track with a teal beam that
              "draws" downward as the section scrolls into view. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 lg:block"
            style={{ backgroundColor: "rgba(255,255,255,0.07)" }}
          >
            <motion.div
              className="absolute inset-x-0 top-0 origin-top"
              style={{
                height: "100%",
                scaleY: reduce ? 1 : spineScale,
                background:
                  "linear-gradient(to bottom, transparent, rgba(0,201,167,0.55) 12%, rgba(0,201,167,0.55) 88%, transparent)",
                boxShadow: "0 0 12px rgba(0,201,167,0.45)",
              }}
            />
          </div>

          {STEPS.map(({ number, icon, key }, index) => (
            <Step
              key={key}
              number={number}
              icon={icon}
              stepKey={key}
              reversed={index % 2 === 1}
              reduce={reduce}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Step({
  number,
  icon: Icon,
  stepKey,
  reversed,
  reduce,
}: {
  number: string;
  icon: LucideIcon;
  stepKey: "step1" | "step2" | "step3";
  reversed: boolean;
  reduce: boolean;
}) {
  const contentSide: "left" | "right" = reversed ? "right" : "left";
  const visualSide: "left" | "right" = reversed ? "left" : "right";

  const content = (
    <motion.div
      className={reversed ? "lg:order-3" : "lg:order-1"}
      variants={fadeInFrom(contentSide, reduce)}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      <ContentBlock number={number} icon={Icon} stepKey={stepKey} reduce={reduce} />
    </motion.div>
  );

  const visual = (
    <motion.div
      className={reversed ? "lg:order-1" : "lg:order-3"}
      variants={fadeInFrom(visualSide, reduce)}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      <Visual stepKey={stepKey} reduce={reduce} />
    </motion.div>
  );

  return (
    <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_72px_1fr] lg:gap-0">
      {content}

      {/* Glowing node on the spine — desktop only */}
      <div className="order-last hidden lg:order-2 lg:flex lg:h-full lg:items-center lg:justify-center">
        <SpineNode reduce={reduce} />
      </div>

      {visual}
    </div>
  );
}

function SpineNode({ reduce }: { reduce: boolean }) {
  return (
    <motion.div
      className="relative flex items-center justify-center"
      style={{ width: "44px", height: "44px" }}
      initial={reduce ? { opacity: 1 } : { scale: 0, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
    >
      {/* Outer halo ring */}
      <span
        className="absolute rounded-full"
        style={{
          width: "44px",
          height: "44px",
          border: "1px solid rgba(0,201,167,0.25)",
          backgroundColor: "rgba(0,201,167,0.04)",
        }}
      />
      {/* Core dot */}
      <span
        className="relative rounded-full"
        style={{
          width: "12px",
          height: "12px",
          backgroundColor: ACCENT,
          boxShadow: "0 0 0 5px rgba(0,201,167,0.14), 0 0 14px rgba(0,201,167,0.7)",
        }}
      />
    </motion.div>
  );
}

function ContentBlock({
  number,
  icon: Icon,
  stepKey,
  reduce,
}: {
  number: string;
  icon: LucideIcon;
  stepKey: "step1" | "step2" | "step3";
  reduce: boolean;
}) {
  const t = useTranslation();
  const step = t.landing.howItWorks[stepKey];

  return (
    <div className="relative px-0 py-6 md:px-6 lg:px-12 lg:py-10">
      {/* Giant sequence number — glow bloom behind a tinted stroke */}
      <div className="relative mb-3 inline-block">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-6 -z-10 rounded-full blur-2xl"
          style={{ background: "radial-gradient(circle, rgba(0,201,167,0.12), transparent 70%)" }}
        />
        <motion.span
          aria-hidden="true"
          className="block select-none text-[68px] leading-none md:text-[88px] lg:text-[132px]"
          style={{
            fontWeight: 800,
            color: "transparent",
            WebkitTextStroke: "1.25px rgba(0,201,167,0.22)",
            letterSpacing: "-0.04em",
          }}
          initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.9, y: 14 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.8, ease: EASE }}
        >
          {number}
        </motion.span>
      </div>

      {/* Badge */}
      <motion.span
        className="flex w-fit items-center font-semibold uppercase"
        style={{
          fontSize: "11px",
          letterSpacing: "0.12em",
          color: ACCENT,
          backgroundColor: "rgba(0,201,167,0.08)",
          border: "0.5px solid rgba(0,201,167,0.22)",
          borderRadius: "20px",
          padding: "5px 13px",
          gap: "7px",
          marginBottom: "16px",
        }}
        initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.85 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.4, ease: EASE, delay: 0.12 }}
      >
        <Icon size={15} strokeWidth={2.25} />
        {step.badge}
      </motion.span>

      {/* Title */}
      <h3
        className="font-display font-bold text-white text-[21px] lg:text-[26px]"
        style={{ lineHeight: 1.22, letterSpacing: "-0.01em", marginBottom: "12px", textWrap: "balance" }}
      >
        {step.title}
      </h3>

      {/* Body */}
      <p
        style={{
          fontSize: "14px",
          color: "rgba(255,255,255,0.55)",
          lineHeight: 1.75,
          maxWidth: "360px",
          textWrap: "pretty",
        }}
      >
        {step.description}
      </p>
    </div>
  );
}

/* ── Visual shell — surface with inner top-highlight + HUD corners ────────── */

function VisualShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative px-0 py-2 md:px-6 lg:px-6">
      <div
        className="group relative overflow-hidden"
        style={{
          borderRadius: "18px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0) 40%), #0c1322",
          boxShadow:
            "0 1px 0 0 rgba(255,255,255,0.05) inset, 0 24px 60px -28px rgba(0,0,0,0.7)",
          padding: "28px",
          minHeight: "188px",
        }}
      >
        {/* Top hairline accent */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-6 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(0,201,167,0.4), transparent)",
          }}
        />
        {/* HUD corners */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-3 h-3 w-3 rounded-tl-sm border-l border-t"
          style={{ borderColor: "rgba(0,201,167,0.35)" }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 right-3 h-3 w-3 rounded-br-sm border-b border-r"
          style={{ borderColor: "rgba(0,201,167,0.35)" }}
        />
        <div className="flex h-full min-h-[132px] items-center justify-center">
          {children}
        </div>
      </div>
    </div>
  );
}

function Visual({ stepKey, reduce }: { stepKey: "step1" | "step2" | "step3"; reduce: boolean }) {
  if (stepKey === "step1") return <VisualConnect reduce={reduce} />;
  if (stepKey === "step2") return <VisualAnalyze reduce={reduce} />;
  return <VisualPerform reduce={reduce} />;
}

/* ── Étape 01 — Connecte : liste de 3 prop firms ─────────────────────────── */

function VisualConnect({ reduce }: { reduce: boolean }) {
  const t = useTranslation();
  const step = t.landing.howItWorks.step1;

  const firms = [
    {
      initials: "A",
      name: "Apex",
      badgeBg: "rgba(61,110,255,0.18)",
      badgeColor: BLUE,
      status: step.statusConnected,
      statusBg: "rgba(0,201,167,0.12)",
      statusColor: ACCENT,
      syncing: false,
    },
    {
      initials: "T",
      name: "TopStep",
      badgeBg: "rgba(0,201,167,0.18)",
      badgeColor: ACCENT,
      status: step.statusConnected,
      statusBg: "rgba(0,201,167,0.12)",
      statusColor: ACCENT,
      syncing: false,
    },
    {
      initials: "FN",
      name: "FundedNext",
      badgeBg: "rgba(239,159,39,0.18)",
      badgeColor: AMBER,
      status: step.statusSyncing,
      statusBg: "rgba(61,110,255,0.12)",
      statusColor: BLUE,
      syncing: true,
    },
  ];

  return (
    <VisualShell>
      <motion.ul
        className="flex w-full max-w-[320px] list-none flex-col gap-2.5 p-0"
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
        }}
      >
        {firms.map((firm) => (
          <motion.li
            key={firm.name}
            className="flex items-center gap-3 rounded-lg px-2.5 py-2"
            style={{ backgroundColor: "rgba(255,255,255,0.02)" }}
            variants={{
              hidden: reduce ? { opacity: 1 } : { opacity: 0, x: 18 },
              show: { opacity: 1, x: 0, transition: { duration: 0.45, ease: EASE } },
            }}
          >
            <span
              className="flex items-center justify-center font-bold"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "7px",
                fontSize: "11px",
                backgroundColor: firm.badgeBg,
                color: firm.badgeColor,
              }}
            >
              {firm.initials}
            </span>
            <span className="flex-1 font-medium text-white" style={{ fontSize: "13px" }}>
              {firm.name}
            </span>
            <span
              className="inline-flex items-center gap-1.5 font-medium"
              style={{
                fontSize: "11px",
                padding: "3px 9px",
                borderRadius: "10px",
                backgroundColor: firm.statusBg,
                color: firm.statusColor,
              }}
            >
              <StatusDot color={firm.statusColor} pulse={firm.syncing && !reduce} />
              {firm.status}
            </span>
          </motion.li>
        ))}
      </motion.ul>
    </VisualShell>
  );
}

function StatusDot({ color, pulse }: { color: string; pulse: boolean }) {
  return (
    <span className="relative flex h-1.5 w-1.5">
      {pulse && (
        <span
          className="absolute inline-flex h-full w-full rounded-full opacity-75"
          style={{ backgroundColor: color, animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite" }}
        />
      )}
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
    </span>
  );
}

/* ── Étape 02 — Analyse : métriques + tags de biais ──────────────────────── */

function VisualAnalyze({ reduce }: { reduce: boolean }) {
  const t = useTranslation();
  const step = t.landing.howItWorks.step2;

  const metrics = [
    { label: step.winRate, value: "74.1%", color: ACCENT, width: "74%" },
    { label: step.profitFactor, value: "1.54", color: BLUE, width: "54%" },
  ];

  const tags = [
    { text: step.biasRevenge, color: RED, danger: true },
    { text: step.biasBestHour, color: ACCENT, danger: false },
    { text: step.biasAvg, color: ACCENT, danger: false },
    { text: step.biasOvertrading, color: RED, danger: true },
  ];

  return (
    <VisualShell>
      <motion.div
        className="w-full max-w-[340px]"
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
        }}
      >
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            style={{ marginBottom: i < metrics.length - 1 ? "18px" : 0 }}
            variants={{
              hidden: reduce ? { opacity: 1 } : { opacity: 0 },
              show: { opacity: 1, transition: { duration: 0.3 } },
            }}
          >
            <div className="flex items-baseline justify-between">
              <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.45)" }}>{m.label}</span>
              <span
                className="font-mono font-bold tabular-nums"
                style={{ fontSize: "19px", color: m.color }}
              >
                {m.value}
              </span>
            </div>
            <div
              className="relative mt-2 w-full overflow-hidden"
              style={{ height: "5px", borderRadius: "4px", backgroundColor: "rgba(255,255,255,0.06)" }}
            >
              {/* Bar fills to target; a brighter glow rides its leading edge */}
              <motion.div
                className="relative h-full"
                style={{ borderRadius: "4px", backgroundColor: m.color, boxShadow: `0 0 10px ${m.color}66` }}
                initial={reduce ? { width: m.width } : { width: 0 }}
                whileInView={{ width: m.width }}
                viewport={VIEWPORT}
                transition={{ duration: 1, ease: EASE, delay: 0.3 + i * 0.12 }}
              />
            </div>
          </motion.div>
        ))}

        <div className="mt-5 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <motion.span
              key={tag.text}
              variants={{
                hidden: reduce ? { opacity: 1 } : { opacity: 0, scale: 0.9 },
                show: { opacity: 1, scale: 1, transition: { duration: 0.32, ease: EASE } },
              }}
              style={{
                fontSize: "11px",
                padding: "4px 11px",
                borderRadius: "20px",
                border: `0.5px solid ${tag.danger ? "rgba(226,75,74,0.32)" : "rgba(0,201,167,0.32)"}`,
                color: tag.color,
                backgroundColor: tag.danger ? "rgba(226,75,74,0.07)" : "rgba(0,201,167,0.07)",
              }}
            >
              {tag.text}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </VisualShell>
  );
}

/* ── Étape 03 — Performe : carte payout ──────────────────────────────────── */

function VisualPerform({ reduce }: { reduce: boolean }) {
  const t = useTranslation();
  const step = t.landing.howItWorks.step3;

  return (
    <VisualShell>
      <div className="flex flex-col items-center text-center">
        <motion.span
          className="font-mono tabular-nums"
          style={{
            fontSize: "52px",
            fontWeight: 800,
            color: ACCENT,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            textShadow: "0 0 28px rgba(0,201,167,0.45)",
            marginBottom: "8px",
          }}
          initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.82 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
        >
          $1,500
        </motion.span>
        <motion.span
          className="inline-flex items-center gap-1.5"
          style={{ fontSize: "12px", color: "rgba(255,255,255,0.45)" }}
          initial={reduce ? { opacity: 1 } : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.4, delay: 0.45 }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }}
          />
          {step.payoutLabel}
        </motion.span>
      </div>
    </VisualShell>
  );
}
